import React from "react";
import whiteArrowIcon from "@src/assets/svg/white-arrow.svg";
import disabledIndicatorIcon from "@src/assets/svg/disabled-indicator-icon.svg";
import greenIndicatorIcon from "@src/components/atoms/indicator-text/icons/green-indicator-icon.svg";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import { Button } from "@src/components/atoms/buttons/button/button";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";
import {
  MachineGroup,
  CrossNameSpaceOrganziation,
} from "@src/types/namespace-configuration";
import TicketRoutingDropdown from "@src/components/atoms/ticket-routing-dropdown/ticket-routing-dropdown";
import styles from "./machine-group-expanded-view.module.scss";
import { useMachineGroupExpandedViewHook } from "@src/components/organisms/machine-group-expanded-view/use-machine-group-expanded-view-hook";

/**
 * Renders a machine group's assigned machines and ticket-routing path.
 * Routing state and API behavior live in the paired custom hook; this component
 * turns that view model into the routing stages, controls, and feedback UI.
 */
const cssClassName = "machine-group-expanded-view";

/** Inputs required to show a group's machines and configurable routing path. */
interface MachineGroupExpandedViewProps {
  machineGroup: MachineGroup;
  onRoutingPathSaved: () => void;
  organizations: CrossNameSpaceOrganziation[];
  permissions: string[];
}

const MachineGroupExpandedView = ({
  machineGroup,
  onRoutingPathSaved,
  organizations,
  permissions,
}: MachineGroupExpandedViewProps) => {
  // Keep the view declarative: the hook owns loading, routing state, validation,
  // permissions, organization choices, and save/toggle handlers.
  const {
    texts,
    namedSources,
    botIsActive,
    isLoadingOwningOrganization,
    owningOrganizationStage,
    routingStages,
    routingDropdownModels,
    isBotBeforeOwningOrganization,
    isSaveButtonDisabled,
    saveTooltipText,
    saveRoutingPath,
    toggleRoutingStage,
  } = useMachineGroupExpandedViewHook({
    machineGroup,
    onRoutingPathSaved,
    organizations,
    permissions,
  });
  // Until the owning organization is known, the routing stages cannot be
  // anchored correctly, so show one accessible loader instead of a partial path.
  const organizationLoader = (
    <div
      aria-label={texts.loadingTicketRoutingPath}
      className={styles[`${cssClassName}__organization-loading`]}
      role="status"
    >
      <SpinnerWithVariant variant={["small", "secondary"]} />
    </div>
  );
  // BOT is a fixed informational step rather than an organization dropdown.
  // Its active/disabled appearance comes from the state prepared by the hook.
  const botField = (
    <div
      aria-disabled={!botIsActive}
      aria-label={texts.addBot}
      className={[
        styles[`${cssClassName}__bot-value`],
        !botIsActive && styles[`${cssClassName}__bot-value--disabled`],
      ]
        .filter(Boolean)
        .join(" ")}
      role="textbox"
      aria-readonly="true"
    >
      {botIsActive ? texts.botOrganizationName : texts.addBot}
    </div>
  );

  return (
    <div className={styles[cssClassName]} data-expanded-view>
      {/* Show only named machines; use the existing empty state when none remain. */}
      {namedSources.length === 0 ? (
        <div className={styles[`${cssClassName}__no-machines-state`]}>
          {texts.noMachinesAdded}
        </div>
      ) : (
        <div className={styles[`${cssClassName}__machines-section`]}>
          <div className={styles[`${cssClassName}__machines-list`]}>
            {namedSources.map((source) => (
              <span
                className={styles[`${cssClassName}__machine-chip`]}
                key={source.source_id}
              >
                {source.name}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Relationship permissions control whether the routing path is visible. */}
      <RenderIfUserHasPermission permission="view-namespace-relationships">
        <section
          aria-busy={isLoadingOwningOrganization}
          aria-label={texts.ticketRoutingPath}
          className={[
            styles[`${cssClassName}__ticket-routing`],
            isLoadingOwningOrganization &&
              styles[`${cssClassName}__ticket-routing--loading`],
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <h3 className={styles[`${cssClassName}__ticket-routing-title`]}>
            {texts.ticketRoutingPath}
          </h3>
          {/* Wait for the owning organization so the path has a reliable start. */}
          {isLoadingOwningOrganization ? (
            organizationLoader
          ) : (
            <div
              className={styles[`${cssClassName}__routing-panel`]}
              data-routing-panel
            >
              {/* The top row names each fixed position in the routing sequence. */}
              <div className={styles[`${cssClassName}__routing-stages`]}>
                {routingStages.map((stage, index) => (
                  <React.Fragment key={stage.type}>
                    <div
                      className={styles[`${cssClassName}__routing-stage`]}
                      style={{ gridColumn: index * 2 + 1 }}
                    >
                      <img
                        alt=""
                        aria-hidden="true"
                        className={styles[`${cssClassName}__routing-icon`]}
                        src={
                          stage.isActive ? stage.activeIcon : stage.inactiveIcon
                        }
                      />
                      <span
                        className={[
                          styles[`${cssClassName}__routing-code`],
                          stage.isActive &&
                            styles[`${cssClassName}__routing-code--active`],
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        data-active={stage.isActive}
                        data-type={stage.type}
                      >
                        {stage.code}
                      </span>
                    </div>
                    {index < routingStages.length - 1 && (
                      <img
                        alt=""
                        aria-hidden="true"
                        className={styles[`${cssClassName}__routing-arrow`]}
                        src={whiteArrowIcon}
                        style={{ gridColumn: index * 2 + 2 }}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              {/* Dots show which stages are active and toggle editable stages. */}
              <div className={styles[`${cssClassName}__routing-progress`]}>
                {routingStages.map((stage, index) => (
                  <button
                    aria-label={stage.ariaLabel}
                    aria-pressed={stage.isActive}
                    disabled={stage.disabled}
                    className={styles[`${cssClassName}__routing-node`]}
                    key={`${stage.type}-node`}
                    onClick={() => toggleRoutingStage(stage.type)}
                    type="button"
                    style={{ gridColumn: index * 2 + 1 }}
                  >
                    <img
                      alt=""
                      aria-hidden="true"
                      src={
                        stage.isActive
                          ? greenIndicatorIcon
                          : disabledIndicatorIcon
                      }
                    />
                  </button>
                ))}
              </div>
              {/* Keep controls in path order; BOT is displayed after the factory. */}
              <div className={styles[`${cssClassName}__routing-controls`]}>
                {routingDropdownModels.map((dropdown, index) => (
                  <React.Fragment key={dropdown.type}>
                    <TicketRoutingDropdown
                      ariaLabel={dropdown.ariaLabel}
                      disabled={dropdown.disabled}
                      disabledTooltip={dropdown.disabledTooltip}
                      id={dropdown.id}
                      onChange={dropdown.onChange}
                      options={dropdown.options}
                      placeholder={dropdown.placeholder}
                      value={dropdown.value}
                    />
                    {index === 0 &&
                      (botIsActive ? (
                        botField
                      ) : (
                        <ToolTip
                          content={
                            isBotBeforeOwningOrganization
                              ? texts.routingStageBeforeStartTooltip
                              : texts.routingStageEnableTooltip
                          }
                          position="bottom"
                        >
                          {botField}
                        </ToolTip>
                      ))}
                  </React.Fragment>
                ))}
              </div>
              {/* Save is available only to managers once a valid start stage exists. */}
              {owningOrganizationStage && (
                <RenderIfUserHasPermission permission="manage-namespace-relationships">
                  <div className={styles[`${cssClassName}__routing-save`]}>
                    <div
                      className={
                        styles[`${cssClassName}__routing-save-tooltip`]
                      }
                    >
                      <Button
                        disabled={isSaveButtonDisabled}
                        fun={saveRoutingPath}
                        text={texts.saveRoutingPath}
                        type="button"
                        variant="primary"
                      />
                      {saveTooltipText && (
                        <span
                          className={
                            styles[`${cssClassName}__routing-save-tooltip-text`]
                          }
                        >
                          {saveTooltipText}
                        </span>
                      )}
                    </div>
                  </div>
                </RenderIfUserHasPermission>
              )}
            </div>
          )}
        </section>
      </RenderIfUserHasPermission>
    </div>
  );
};

export default MachineGroupExpandedView;
